// 독립 연결 창이 제목줄에 함께 쓰는 **조회 대상 표시·선택기**.
// (오라클 성능 분석 · DBMS 카탈로그 · 자원 분석 · PL/SQL 편집기 · 세션 분석)
//
// 예전에는 창마다 연결 `<select>` 를 자기 탭줄·툴바에 따로 뒀다. 다섯 벌이라 모양도
// 자리도 제각각이었고, 무엇보다 **세션은 아예 보이지 않았다** — 창이 무엇으로 보고
// 있는지 화면이 말하지 않았다.
//
// 지켜야 할 것
// - **실행 중인 세션은 고를 수 없다.** 판정은 `utils/sessionDisplay` 의 `isSessionBusy`
//   한 곳이다(에디터의 SessionPicker 와 같은 함수). 갈라지면 한쪽만 409 를 받는다.
// - 세션 이름·SID 도 `sessionDisplay` 의 것을 쓴다. 화면마다 따로 세면 번호가 갈린다.
import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FiChevronDown, FiDatabase, FiAlertTriangle } from 'react-icons/fi';
import { sessionDisplayName, sessionSid, isSessionBusy, isHumanSession } from '../../utils/sessionDisplay';
import './OwnConnTarget.css';

export default function OwnConnTarget({
    conns, connId, onConnChange,
    sessionId, session, sessionError, creating,
    onPickSession, hideConn = false,
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const boxRef = useRef(null);
    const allSessions = useSelector(s => s.sessions?.list || []);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const connName = useMemo(
        () => conns.find(c => c.id === connId)?.name || connId || null,
        [conns, connId],
    );

    // 고를 수 있는 세션 — 지금 이 창의 것은 빼고(이미 쓰는 중), ephemeral 도 뺀다
    // (일회용은 요청이 끝나면 사라져 고를 대상이 아니다).
    // **배경 세션도 뺀다**: 수집기(`system-collect`)는 timeout 이 10초라 골라 놓으면
    // 모니터링 조회가 10초에서 끊기는데, 화면에는 그냥 실패로만 보인다.
    const candidates = useMemo(() => {
        const same = []; const others = [];
        // 연결 탭 안(hideConn)에서는 이 창이 고를 수 있는 연결의 세션만 — 다른 탭이 연 연결은 conns 에 없다
        const allowed = hideConn ? new Set(conns.map(c => c.id)) : null;
        for (const s of allSessions) {
            if (s.ephemeral || s.sessionId === sessionId) continue;
            if (!isHumanSession(s)) continue;
            if (allowed && !allowed.has(s.connectionId)) continue;
            (s.connectionId === connId ? same : others).push(s);
        }
        return { same, others };
    }, [allSessions, sessionId, connId, hideConn, conns]);

    const sid = sessionSid(session);
    const label = (() => {
        if (creating) return t('monitoring:mt.creating');
        if (!session) return t('monitoring:mt.noSession');
        const name = sessionDisplayName(session, t);
        return sid != null ? `${name} · sid ${sid}` : name;
    })();

    const renderGroup = (title, items) => items.length > 0 && (
        <div className="montgt-group">
            <div className="montgt-group-title">{title}</div>
            {items.map(s => {
                // 실행 중 세션에 조회를 더 보내면 서버가 409 로 거절한다 — 고르지 못하게 막는다
                const busy = isSessionBusy(s);
                const csid = sessionSid(s);
                const cname = conns.find(c => c.id === s.connectionId)?.name || s.connectionId;
                return (
                    <button
                        key={s.sessionId}
                        type="button"
                        className="montgt-option"
                        disabled={busy}
                        title={busy ? t('monitoring:mt.busyHint') : undefined}
                        onClick={() => { if (!busy) { onPickSession(s.sessionId, s.connectionId); setOpen(false); } }}
                    >
                        <span className="montgt-opt-conn">{cname}</span>
                        <span className="montgt-opt-name">{sessionDisplayName(s, t)}</span>
                        {csid != null && <span className="montgt-opt-sid">sid {csid}</span>}
                        {busy && <span className="montgt-opt-busy">{t('monitoring:mt.busy')}</span>}
                    </button>
                );
            })}
        </div>
    );

    // 연결 탭 안(hideConn)에서는 연결도 세션도 보이지 않는다 — 탭 이름이 연결이고 세션은 창이 알아서 쓴다(2026-10-14 요청).
    // 다만 전담 세션을 못 만들어 조회마다 일회용 세션으로 도는 때는 그 사실을 말해야 하므로 경고만 남긴다.
    if (hideConn && !sessionError) return null;
    return (
        <div className="montgt" ref={boxRef}>
            {/* 연결은 그대로 고를 수 있다 — 고르면 그 연결에 새 세션을 만든다. 연결 탭 안에서는 탭 이름이 연결이라 숨긴다 */}
            {!hideConn && (<>
            <FiDatabase size={13} className="montgt-icon" />
            <select
                className="montgt-conn"
                value={connId || ''}
                onChange={(e) => onConnChange(e.target.value)}
                aria-label={t('monitoring:mt.connection')}
                title={connName || ''}
            >
                {conns.length === 0 && <option value="">{t('monitoring:mt.noConnection')}</option>}
                {conns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            <span className="montgt-sep">·</span>
            </>)}

            <button
                type="button"
                className={`montgt-session ${sessionError ? 'montgt-session-warn' : ''}`}
                onClick={() => setOpen(v => !v)}
                aria-haspopup="listbox"
                aria-expanded={open}
                title={sessionError || t('monitoring:mt.changeSession')}
            >
                {sessionError && <FiAlertTriangle size={11} />}
                <span className="montgt-session-label">{label}</span>
                <FiChevronDown size={12} />
            </button>

            {open && (
                <div className="montgt-menu" role="listbox">
                    {sessionError && (
                        <div className="montgt-warn">
                            <FiAlertTriangle size={12} />
                            {/* 세션을 못 만들어도 창은 돈다 — 다만 조회마다 다른 일회용 세션이라는 것을 말한다 */}
                            <span>{t('monitoring:mt.fellBack')}</span>
                        </div>
                    )}
                    {renderGroup(t('monitoring:mt.sameConn'), candidates.same)}
                    {renderGroup(t('monitoring:mt.otherConn'), candidates.others)}
                    {candidates.same.length === 0 && candidates.others.length === 0 && (
                        <div className="montgt-empty">{t('monitoring:mt.noOther')}</div>
                    )}
                </div>
            )}
        </div>
    );
}

OwnConnTarget.propTypes = {
    conns: PropTypes.array.isRequired,
    connId: PropTypes.string,
    onConnChange: PropTypes.func.isRequired,
    sessionId: PropTypes.string,
    session: PropTypes.object,
    sessionError: PropTypes.string,
    creating: PropTypes.bool,
    onPickSession: PropTypes.func.isRequired,
    hideConn: PropTypes.bool,
};
